import { useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Loader2, Pencil } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
];

const EMPTY_LOC = {
  type: "showroom",
  label: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  postal_code: "",
  country: "US",
  timezone: "America/New_York",
};

export function DealerLocationsTab({ dealer, users = [], onChanged }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [locForm, setLocForm] = useState(EMPTY_LOC);
  const [addingLoc, setAddingLoc] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_LOC);
  const [savingEdit, setSavingEdit] = useState(false);

  const setLoc = (key) => (e) => setLocForm((f) => ({ ...f, [key]: e.target.value }));
  const setEdit = (key) => (e) => setEditForm((f) => ({ ...f, [key]: e.target.value }));

  const openEdit = (loc) => {
    setEditTarget(loc);
    setEditForm({
      type: loc.type,
      label: loc.label || "",
      timezone: loc.timezone || "America/New_York",
      line1: loc.address?.line1 || "",
      line2: loc.address?.line2 || "",
      city: loc.address?.city || "",
      state: loc.address?.state || "",
      postal_code: loc.address?.postal_code || "",
      country: loc.address?.country || "US",
      is_default: !!loc.is_default,
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editTarget) return;
    setSavingEdit(true);
    try {
      await api.patch(`/dealers/${dealer.id}/locations/${editTarget.id}`, {
        type: editForm.type,
        label: editForm.label,
        timezone: editForm.timezone,
        is_default: editForm.is_default,
        address: {
          line1: editForm.line1,
          line2: editForm.line2,
          city: editForm.city,
          state: editForm.state,
          postal_code: editForm.postal_code,
          country: editForm.country,
        },
      });
      toast.success("Location updated");
      setEditTarget(null);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAddLocation = async (e) => {
    e.preventDefault();
    setAddingLoc(true);
    try {
      await api.post(`/dealers/${dealer.id}/locations`, {
        type: locForm.type,
        label: locForm.label,
        timezone: locForm.timezone,
        address: {
          line1: locForm.line1,
          line2: locForm.line2,
          city: locForm.city,
          state: locForm.state,
          postal_code: locForm.postal_code,
          country: locForm.country,
        },
      });
      toast.success("Location added");
      setDialogOpen(false);
      setLocForm(EMPTY_LOC);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAddingLoc(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="text-sm text-warmgrey">Showroom, billing, warehouse and ship-to addresses.</p>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-location-button" className="h-10 bg-terracotta text-white hover:bg-terracotta-hover">
              <Plus className="mr-2 h-4 w-4" /> Add Location
            </Button>
          </DialogTrigger>
          <DialogContent className="border-border bg-white sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-medium text-charcoal">Add location</DialogTitle>
              <DialogDescription className="text-sm text-warmgrey">The first location becomes the default ship-to.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddLocation} className="mt-2 space-y-5" data-testid="add-location-form">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Type</Label>
                  <Select value={locForm.type} onValueChange={(v) => setLocForm((f) => ({ ...f, type: v }))}>
                    <SelectTrigger data-testid="location-type-select" className="h-11 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="showroom">Showroom</SelectItem>
                      <SelectItem value="billing">Billing</SelectItem>
                      <SelectItem value="ship_to">Ship-to</SelectItem>
                      <SelectItem value="warehouse">Warehouse</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Label *</Label>
                  <Input required data-testid="location-label-input" value={locForm.label} onChange={setLoc("label")} placeholder="e.g. Miami Showroom" className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Address line 1 *</Label>
                <Input required data-testid="location-line1-input" value={locForm.line1} onChange={setLoc("line1")} className="h-11 bg-white focus-visible:ring-terracotta" />
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">City *</Label>
                  <Input required data-testid="location-city-input" value={locForm.city} onChange={setLoc("city")} className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">State *</Label>
                    <Input required data-testid="location-state-input" value={locForm.state} onChange={setLoc("state")} className="h-11 bg-white focus-visible:ring-terracotta" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">ZIP *</Label>
                    <Input required data-testid="location-zip-input" value={locForm.postal_code} onChange={setLoc("postal_code")} className="h-11 bg-white focus-visible:ring-terracotta" />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Time zone</Label>
                <Select value={locForm.timezone} onValueChange={(v) => setLocForm((f) => ({ ...f, timezone: v }))}>
                  <SelectTrigger data-testid="location-timezone-select" className="h-11 bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {TIMEZONES.map((tz) => (
                      <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={addingLoc} data-testid="add-location-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
                {addingLoc ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add location"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        {(dealer.locations || []).length === 0 ? (
          <Card className="border-border bg-white md:col-span-2">
            <CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="locations-empty">
              No locations yet. The first location added becomes the default ship-to.
            </CardContent>
          </Card>
        ) : (
          dealer.locations.map((loc) => (
            <Card key={loc.id} className="border-border bg-white" data-testid={`location-card-${loc.id}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wider text-terracotta">{loc.type.replace("_", " ")}</p>
                  <div className="flex items-center gap-2">
                    {loc.is_default && <span className="badge-status badge-neutral">default</span>}
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`edit-location-${loc.id}`}
                      onClick={() => openEdit(loc)}
                      className="border-limestone text-charcoal hover:bg-sand"
                    >
                      <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                    </Button>
                  </div>
                </div>
                <p className="mt-3 font-medium text-charcoal">{loc.label || "—"}</p>
                <p className="mt-1 text-sm leading-relaxed text-warmgrey">
                  {loc.address?.line1}
                  {loc.address?.line2 ? `, ${loc.address.line2}` : ""}
                  <br />
                  {loc.address?.city}
                  {loc.address?.state ? `, ${loc.address.state}` : ""} {loc.address?.postal_code}
                </p>
                <p className="mt-2 text-xs text-muted-foreground">{loc.timezone}</p>
                {(() => {
                  const assigned = users.filter((u) => u.location_scope === "location" && u.location_id === loc.id);
                  return assigned.length > 0 ? (
                    <p className="mt-2 text-xs text-muted-foreground" data-testid={`location-users-${loc.id}`}>
                      Location Managers: {assigned.map((u) => u.name || u.email).join(", ")}
                    </p>
                  ) : null;
                })()}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent className="border-border bg-white sm:max-w-lg" data-testid="edit-location-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Edit location</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">Update this location's name, type, address, time zone, or default designation.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveEdit} className="mt-2 space-y-5" data-testid="edit-location-form">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Type</Label>
                <Select value={editForm.type} onValueChange={(v) => setEditForm((f) => ({ ...f, type: v }))}>
                  <SelectTrigger data-testid="edit-location-type-select" className="h-11 bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="showroom">Showroom</SelectItem>
                    <SelectItem value="billing">Billing</SelectItem>
                    <SelectItem value="ship_to">Ship-to</SelectItem>
                    <SelectItem value="warehouse">Warehouse</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Label *</Label>
                <Input required data-testid="edit-location-label-input" value={editForm.label} onChange={setEdit("label")} className="h-11 bg-white focus-visible:ring-terracotta" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Address line 1 *</Label>
              <Input required data-testid="edit-location-line1-input" value={editForm.line1} onChange={setEdit("line1")} className="h-11 bg-white focus-visible:ring-terracotta" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Address line 2</Label>
              <Input data-testid="edit-location-line2-input" value={editForm.line2} onChange={setEdit("line2")} className="h-11 bg-white focus-visible:ring-terracotta" />
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">City *</Label>
                <Input required data-testid="edit-location-city-input" value={editForm.city} onChange={setEdit("city")} className="h-11 bg-white focus-visible:ring-terracotta" />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">State *</Label>
                  <Input required data-testid="edit-location-state-input" value={editForm.state} onChange={setEdit("state")} className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">ZIP *</Label>
                  <Input required data-testid="edit-location-zip-input" value={editForm.postal_code} onChange={setEdit("postal_code")} className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Time zone</Label>
              <Select value={editForm.timezone} onValueChange={(v) => setEditForm((f) => ({ ...f, timezone: v }))}>
                <SelectTrigger data-testid="edit-location-timezone-select" className="h-11 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-xs font-medium text-charcoal">
              <Switch checked={editForm.is_default} onCheckedChange={(v) => setEditForm((f) => ({ ...f, is_default: v }))} data-testid="edit-location-default" />
              Default ship-to location
            </label>
            <Button type="submit" disabled={savingEdit} data-testid="edit-location-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
              {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save location"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
